import type { GlAccount } from "../types";
import { GlaAccountType, GlaFilterPreset } from "../types";
import type {DocumentDirection} from "../../../../generated/enums.ts";

const PROFIT_AND_LOSS_PREFIX = "W";
const BALANCE_SHEET_PREFIX = "B";

const FIXED_ASSETS_BS_IDS = new Set([
    "BIvaKou",
    "BIvaKoo",
    "BIvaCev",
    "BIvaGoo",
    "BMvaTer",
    "BMvaBeg",
    "BMvaVer",
    "BMvaMei",
    "BMvaTev",
    "BMvaObe",
    "BMvaBei",
]);

const FIXED_ASSETS_PL_IDS = new Set(["WAfsAiv", "WAfsAmv"]);

export function isProfitAndLossAccount(account: GlAccount): boolean {
    return account.id.startsWith(PROFIT_AND_LOSS_PREFIX);
}

export function isBalanceSheetAccount(account: GlAccount): boolean {
    return account.id.startsWith(BALANCE_SHEET_PREFIX);
}

export function isUncategorizedAccount(account: GlAccount): boolean {
    return !isProfitAndLossAccount(account) && !isBalanceSheetAccount(account);
}

export function getAccountType(account: GlAccount): GlaAccountType {
    if (isProfitAndLossAccount(account)) {
        return GlaAccountType.PROFIT_AND_LOSS;
    }
    if (isBalanceSheetAccount(account)) {
        return GlaAccountType.BALANCE_SHEET;
    }
    return GlaAccountType.UNCATEGORIZED;
}

function matchesDirection(account: GlAccount, direction: DocumentDirection): boolean {
    return (
        account.applicable_bill_direction === direction ||
        account.applicable_bill_direction === 'other'
    );
}

function matchesPreset(account: GlAccount, preset: GlaFilterPreset): boolean {
    switch (preset) {
        case GlaFilterPreset.ALL:
            return true;
        case GlaFilterPreset.PROFIT_AND_LOSS:
            return isProfitAndLossAccount(account);
        case GlaFilterPreset.BALANCE_SHEET:
            return isBalanceSheetAccount(account);
        case GlaFilterPreset.FIXED_ASSETS_BS:
            return FIXED_ASSETS_BS_IDS.has(account.id);
        case GlaFilterPreset.FIXED_ASSETS_PL:
            return FIXED_ASSETS_PL_IDS.has(account.id);
        default: {
            const _exhaustive: never = preset;
            return _exhaustive;
        }
    }
}

export function filterAccounts(
    accounts: GlAccount[],
    preset: GlaFilterPreset,
    direction: DocumentDirection | undefined,
    showAll: boolean
): GlAccount[] {
    if (showAll) return accounts;

    return accounts.filter(
        (account) =>
            matchesPreset(account, preset) &&
            (direction === undefined || matchesDirection(account, direction))
    );
}

export function getVisibleAccounts(
    accounts: GlAccount[],
    filterPreset: GlaFilterPreset,
    documentDirectionFilter: DocumentDirection | undefined,
    showingAll: boolean
): GlAccount[] {
    if (showingAll) {
        return accounts;
    }

    const hasActivePreset =
        filterPreset !== GlaFilterPreset.ALL || !!documentDirectionFilter;

    if (hasActivePreset) {
        return filterAccounts(accounts, filterPreset, documentDirectionFilter, false);
    }

    return [];
}

export function filterBySearchQuery(accounts: GlAccount[], query: string): GlAccount[] {
    const q = query.trim().toLowerCase();
    if (!q) return accounts;

    return accounts.filter(
        (a) =>
            a.id.toLowerCase().includes(q) ||
            a.label_nl.toLowerCase().includes(q) ||
            a.label.toLowerCase().includes(q) ||
            a.rgs_official_label.toLowerCase().includes(q) ||
            a.rgs_number.includes(q)
    );
}
